import { useEffect, useRef } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { useAppDispatch } from "../redux/services/store";
import { baseApi } from "../redux/services/baseApi";
import { useLazyVerifySubscriptionPaymentQuery } from "../redux/services/subscription";
import { FLEETRIFYROUTEPATH } from "../routes/routesPath";

const POLL_INTERVAL_MS = 5000;
const MAX_ATTEMPTS = 12; // ~1 minute of polling while the payment is pending
const TOAST_ID = "payment-verification";

/**
 * RoutePay sends the company back with ?reference=... after checkout. Ask the
 * API what actually happened to that payment and tell the user, polling while
 * it's still pending. The reference is removed from the address afterwards so
 * a refresh doesn't repeat the check.
 */
export function usePaymentVerification() {
    const location = useLocation();
    const navigate = useNavigate();
    const dispatch = useAppDispatch();
    const [verifyPayment] = useLazyVerifySubscriptionPaymentQuery();
    const handledRef = useRef<string | null>(null);

    const reference = new URLSearchParams(location.search).get("reference");

    useEffect(() => {
        if (!reference || handledRef.current === reference) return;
        handledRef.current = reference;

        let cancelled = false;
        let finished = false;
        const clearReference = () => {
            const params = new URLSearchParams(window.location.search);
            params.delete("reference");
            const search = params.toString();
            navigate(`${window.location.pathname}${search ? `?${search}` : ""}`, { replace: true });
        };

        const run = async () => {
            toast.loading("Confirming your payment...", { id: TOAST_ID });

            for (let attempt = 1; attempt <= MAX_ATTEMPTS && !cancelled; attempt++) {
                let result;
                try {
                    result = await verifyPayment(reference, false).unwrap();
                } catch (err: any) {
                    if (cancelled) return;
                    toast.error(
                        err?.status === 404
                            ? "We could not find this payment."
                            : err?.data?.error?.message ?? err?.data?.message ?? "We could not confirm this payment. Please refresh to try again.",
                        { id: TOAST_ID, duration: 8000 }
                    );
                    break;
                }
                if (cancelled) return;

                const { payment_status, message } = result.data;

                if (payment_status === "successful") {
                    toast.success(message, { id: TOAST_ID, duration: 8000 });
                    // Reload /me so the plan, sidebar and subscription gate pick up the new plan.
                    dispatch(baseApi.util.invalidateTags(["users"]));
                    break;
                }
                if (payment_status === "failed") {
                    toast.error(message, {
                        id: TOAST_ID,
                        duration: 15000,
                        action: {
                            label: "Try again",
                            onClick: () => navigate(`${FLEETRIFYROUTEPATH.SETTINGS}?tab=subscription`),
                        },
                    });
                    break;
                }
                if (payment_status === "under_review") {
                    toast.info(message, { id: TOAST_ID, duration: 10000 });
                    break;
                }

                // pending: keep the user informed and check again shortly.
                if (attempt === MAX_ATTEMPTS) {
                    toast.info(message, { id: TOAST_ID, duration: 10000 });
                    break;
                }
                toast.loading(message, { id: TOAST_ID });
                await new Promise((resolve) => setTimeout(resolve, POLL_INTERVAL_MS));
            }

            if (cancelled) return;
            finished = true;
            clearReference();
        };

        run();

        return () => {
            // Removing ?reference= re-runs this cleanup; keep the final message.
            if (finished) return;
            cancelled = true;
            // Interrupted mid-check (e.g. React StrictMode remount): allow a restart.
            handledRef.current = null;
            toast.dismiss(TOAST_ID);
        };
    }, [reference, verifyPayment, dispatch, navigate]);
}
